/**
 * RegaloDellAzienda — lo strumento regalato dalla piattaforma a un'azienda (atlante cap. 251, tappa 4): fino a quando e
 * perché, «Regala» e «Togli il regalo». Le regole stanno in `shared/strumenti/regalo.ts`: un regalo si fa solo a chi
 * non ha lo strumento nell'abbonamento (a chi lo paga si danno condizioni su misura), sette giorni prima della fine
 * l'azienda viene avvisata, alla fine lo strumento si spegne da solo.
 */

import React, { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio,
  RadioGroup, Stack, TextField, Typography,
} from '@mui/material';
import { tokens } from '../../theme';
import { SoftChip } from '../SoftChip';
import type { StrumentoACanone } from '@shared/strumenti/listino';
import { regaloDallAzienda, GIORNI_DI_PREAVVISO } from '@shared/strumenti/regalo';
import type { TenantConsoleListItem } from '../../services/tenantService';
import { regalaStrumento } from '../../services/strumentiService';
import { data, ultimoGiorno } from './testiStrumenti';

/** Lo scarto di Roma da UTC in minuti in un istante (60 d'inverno, 120 d'estate), letto dal fuso, non dal browser. */
function scartoDiRoma(ms: number): number {
  const parte = new Intl.DateTimeFormat('en-US', { timeZone: 'Europe/Rome', timeZoneName: 'shortOffset' })
    .formatToParts(new Date(ms)).find((p) => p.type === 'timeZoneName')?.value ?? 'GMT+1';
  const m = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(parte);
  return m ? (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] ?? 0)) : 60;
}

/** «2026-11-30» (dal campo data) → la mezzanotte di Roma del giorno DOPO: il regalo vale per tutto il 30. */
function fineDelGiorno(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const mezzanotteUtc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + 1);
  // Due passi: lo scarto si legge all'istante giusto anche nei giorni del cambio d'ora.
  const prova = mezzanotteUtc - scartoDiRoma(mezzanotteUtc) * 60_000;
  return mezzanotteUtc - scartoDiRoma(prova) * 60_000;
}

export const RegaloDellAzienda: React.FC<{
  tenant: TenantConsoleListItem; strumento: StrumentoACanone; nellAbbonamento: boolean; onCambiato: () => void;
}> = ({ tenant, strumento, nellAbbonamento, onCambiato }) => {
  const ora = Date.now();
  const regalo = regaloDallAzienda(tenant, strumento.capability, ora);
  const [dialogo, setDialogo] = useState(false);
  const [conferma, setConferma] = useState(false);
  const [conScadenza, setConScadenza] = useState(true);
  const [giorno, setGiorno] = useState('');
  const [motivo, setMotivo] = useState('');
  const [lavoro, setLavoro] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const fino = conScadenza ? fineDelGiorno(giorno) : null;
  const valido = motivo.trim() !== '' && (!conScadenza || (fino !== null && fino > ora + 86_400_000));

  const salva = async (dati: { fino_al_ms: number | null; motivo: string } | null) => {
    setLavoro(true);
    setErrore(null);
    try {
      await regalaStrumento({ tenant_id: tenant.id, strumento: strumento.capability, regalo: dati });
      setDialogo(false);
      setConferma(false);
      onCambiato();
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e));
    } finally {
      setLavoro(false);
    }
  };

  if (!regalo && nellAbbonamento) return null;
  return (
    <Box sx={regalo ? { mt: 1.5 } : { display: 'inline-flex' }}>
      {regalo ? (
        <>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <SoftChip label="regalo" tone="primary" />
            <Typography variant="body2">
              {regalo.fino_al_ms === null ? 'Gratis, senza scadenza' : `Gratis fino al ${ultimoGiorno(regalo.fino_al_ms)}`}
              {regalo.dal_ms ? `, dal ${data(regalo.dal_ms)}` : ''}.
            </Typography>
          </Stack>
          {regalo.motivo && <Typography variant="body2" sx={{ mt: 0.5, color: tokens.color.ash }}>Perché: {regalo.motivo}</Typography>}
          <Button size="small" onClick={() => setConferma(true)} sx={{ mt: 1, textTransform: 'none', minHeight: 36, color: tokens.color.ash }}>
            Togli il regalo
          </Button>
        </>
      ) : (
        <Button size="small" variant="outlined" onClick={() => { setMotivo(''); setGiorno(''); setConScadenza(true); setErrore(null); setDialogo(true); }}
          sx={{ textTransform: 'none', minHeight: 36 }}>
          Regala
        </Button>
      )}
      {errore && !dialogo && <Alert severity="error" sx={{ mt: 1 }}>{errore}</Alert>}

      <Dialog open={dialogo} onClose={lavoro ? undefined : () => setDialogo(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontFamily: tokens.font.display, fontWeight: 600 }}>Regala {strumento.etichetta} a {tenant.nome_azienda}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: tokens.color.ash }}>
            Si accende subito, gratis, senza abbonamento. {GIORNI_DI_PREAVVISO} giorni prima della fine l’azienda viene avvisata; alla fine si spegne
            da solo e, per continuare, lo attiva lei alle sue condizioni.
          </Typography>
          <RadioGroup value={conScadenza ? 'si' : 'no'} onChange={(e) => setConScadenza(e.target.value === 'si')} sx={{ mt: 1.5 }}>
            <FormControlLabel value="si" control={<Radio size="small" />} label="Fino al giorno" />
            {conScadenza && (
              <TextField type="date" size="small" value={giorno} onChange={(e) => setGiorno(e.target.value)} sx={{ ml: 4, mb: 1, maxWidth: 200 }}
                inputProps={{ 'aria-label': 'Ultimo giorno gratis' }} />
            )}
            <FormControlLabel value="no" control={<Radio size="small" />} label="Senza scadenza" />
          </RadioGroup>
          <TextField sx={{ mt: 1.5 }} fullWidth size="small" label="Perché (resta scritto)" placeholder="Prova di un mese concordata con Luigi"
            value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          {errore && <Alert severity="error" sx={{ mt: 2 }}>{errore}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogo(false)} disabled={lavoro}>Annulla</Button>
          <Button variant="contained" disableElevation disabled={lavoro || !valido} onClick={() => { void salva({ fino_al_ms: fino, motivo: motivo.trim() }); }}
            startIcon={lavoro ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Regala
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={conferma} onClose={lavoro ? undefined : () => setConferma(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: tokens.font.display, fontWeight: 600 }}>Togli il regalo</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {strumento.etichetta} si spegne subito per {tenant.nome_azienda}, salvo che lo abbia attivato a pagamento. L’azienda non riceve avvisi.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConferma(false)} disabled={lavoro}>Annulla</Button>
          <Button variant="contained" disableElevation onClick={() => { void salva(null); }} disabled={lavoro}
            startIcon={lavoro ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Togli il regalo
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
